// src/pages/Admin.jsx
import React, { useEffect, useState } from "react";
import { db, collection, getDocs, deleteDoc, doc } from "../../config/firebaseconfig";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { FiMail, FiUser, FiClock, FiMessageSquare, FiTrash2, FiLogOut } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const Admin = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    navigate('/admin-login');
  };

  const fetchMessages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "contacts"));
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort messages by timestamp, newest first
      const sortedData = data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setMessages(sortedData);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
  };

  const handleCloseModal = () => {
    setSelectedMessage(null);
  };

  const handleDeleteClick = (e, message) => {
    e.stopPropagation(); // Prevent opening the message modal
    setMessageToDelete(message);
  };

  const handleDeleteConfirm = async () => {
    if (!messageToDelete) return;

    try {
      await deleteDoc(doc(db, "contacts", messageToDelete.id));
      setMessages(messages.filter(msg => msg.id !== messageToDelete.id));
      setMessageToDelete(null);
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const handleDeleteCancel = () => {
    setMessageToDelete(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-[#132037] to-[#1B2B4B] p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
            Admin Dashboard
          </h1>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            <FiLogOut />
            <span>Logout</span>
          </motion.button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center p-8 bg-[#1E293B]/70 backdrop-blur-sm rounded-xl border border-[#334155]/30"
          >
            <p className="text-xl text-[#94A3B8]">No messages found.</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02 }}
                className="bg-[#1E293B]/70 backdrop-blur-sm border border-[#334155]/30 rounded-xl p-6 cursor-pointer hover:border-blue-500/50 transition-all duration-300 relative group"
                onClick={() => handleMessageClick(msg)}
              >
                <button
                  onClick={(e) => handleDeleteClick(e, msg)}
                  className="absolute top-4 right-4 p-2 text-[#94A3B8] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <FiTrash2 className="w-5 h-5" />
                </button>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                      <FiUser className="text-white text-xl" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-white truncate">
                      {msg.firstName} {msg.lastName}
                    </h3>
                    <p className="text-[#94A3B8] flex items-center space-x-2">
                      <FiMail className="inline" />
                      <span className="truncate">{msg.email}</span>
                    </p>
                    <p className="text-[#94A3B8] mt-2 line-clamp-2">
                      {msg.message}
                    </p>
                    <p className="text-[#64748B] text-sm mt-3 flex items-center space-x-2">
                      <FiClock className="inline" />
                      <span>
                        {msg.timestamp
                          ? format(new Date(msg.timestamp), "PPpp")
                          : "N/A"}
                      </span>
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Message Detail Modal */}
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#1E293B] rounded-xl p-6 max-w-2xl w-full border border-[#334155]/30"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Message Details
                </h2>
                <div className="flex space-x-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(e, selectedMessage);
                      handleCloseModal();
                    }}
                    className="text-red-500 hover:text-red-400 transition-colors"
                  >
                    <FiTrash2 className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="text-[#94A3B8] hover:text-white transition-colors"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <FiUser className="text-blue-500 text-xl" />
                  <div>
                    <p className="text-[#94A3B8] text-sm">Name</p>
                    <p className="text-white">
                      {selectedMessage.firstName} {selectedMessage.lastName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FiMail className="text-blue-500 text-xl" />
                  <div>
                    <p className="text-[#94A3B8] text-sm">Email</p>
                    <p className="text-white">{selectedMessage.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FiClock className="text-blue-500 text-xl" />
                  <div>
                    <p className="text-[#94A3B8] text-sm">Time</p>
                    <p className="text-white">
                      {selectedMessage.timestamp
                        ? format(new Date(selectedMessage.timestamp), "PPpp")
                        : "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FiMessageSquare className="text-blue-500 text-xl mt-1" />
                  <div>
                    <p className="text-[#94A3B8] text-sm">Message</p>
                    <p className="text-white whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Delete Confirmation Modal */}
        {messageToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#1E293B] rounded-xl p-6 max-w-md w-full border border-[#334155]/30"
            >
              <h2 className="text-2xl font-bold text-white mb-4">
                Delete Message
              </h2>
              <p className="text-[#94A3B8] mb-6">
                Are you sure you want to delete this message? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-[#94A3B8] hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Admin;
